import { Typography, Modal, Grid } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import styles from './AdaptiveHorizontalMenu.module.scss';
import { AppContext } from 'src/contexts/app.context';
import type { ResourceShort } from 'src/types/master_data/resource.type';
import { useResizeObserverErrorSuppression } from 'src/hooks/useResizeObserverErrorSuppression';
import { useNavigate } from 'react-router-dom';
import { generateIcon } from 'src/utils/iconRegistry';
import { Lock } from 'lucide-react';
import { showNotification } from '@martech-solution/utils/utils';

const { Text } = Typography;
const { useBreakpoint } = Grid;

// Constants for menu usage tracking
const MENU_ACCESS_COUNT_KEY = 'menu_access_count';

interface AdaptiveHorizontalMenuProps {
  className?: string;
  maxVisibleItems?: number; // Số lượng item hiển thị tối đa (mặc định là 4)
}

// Utility functions for usage tracking
const getAccessCount = (resourceId: string): number => {
  try {
    const counts = JSON.parse(localStorage.getItem(MENU_ACCESS_COUNT_KEY) || '{}');
    return counts[resourceId] || 0;
  } catch {
    return 0;
  }
};

const incrementAccessCount = (resourceId: string): void => {
  try {
    const counts = JSON.parse(localStorage.getItem(MENU_ACCESS_COUNT_KEY) || '{}');
    counts[resourceId] = (counts[resourceId] || 0) + 1;
    localStorage.setItem(MENU_ACCESS_COUNT_KEY, JSON.stringify(counts));
  } catch (error) {
    console.warn('Failed to save menu access count:', error);
  }
};

// Optional: Function to reset usage statistics (for future use)
// const resetAccessCounts = (): void => {
//   try {
//     localStorage.removeItem(MENU_ACCESS_COUNT_KEY);
//   } catch (error) {
//     console.warn('Failed to reset menu access counts:', error);
//   }
// };

export default function AdaptiveHorizontalMenu({ className, maxVisibleItems = 4 }: AdaptiveHorizontalMenuProps) {
  const [visibleItems, setVisibleItems] = useState<ResourceShort[]>([]);
  const [hiddenItems, setHiddenItems] = useState<ResourceShort[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { rootResources, setRootResource } = useContext(AppContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const screens = useBreakpoint();

  // Use custom hook to suppress ResizeObserver errors
  useResizeObserverErrorSuppression();

  // Calculate responsive maxVisibleItems based on screen size
  const responsiveMaxVisibleItems = useMemo(() => {
    if (!screens.sm) {
      // xs (< 576px): Only show 1 item + more button
      return Math.min(0, maxVisibleItems);
    } else if (!screens.md) {
      // sm (576px - 768px): Show 2 items + more button
      return Math.min(1, maxVisibleItems);
    } else if (!screens.lg) {
      // md (768px - 992px): Show 3 items + more button
      return Math.min(2, maxVisibleItems);
    } else if (!screens.xl) {
      // lg (992px - 1200px): Show full items or maxVisibleItems
      return Math.min(3, maxVisibleItems);
    } else {
      // xl (>= 1200px): Show all items or maxVisibleItems + 1
      return maxVisibleItems;
    }
  }, [screens, maxVisibleItems]);

  // Render icon
  const renderIcon = (icon: string | React.ReactNode | undefined, size?: number) => {
    if (!icon) return null;

    if (typeof icon === 'string') {
      return generateIcon(icon, size || 18, '#64748b');
    }

    return icon;
  };

  useEffect(() => {
    if (!rootResources) return;

    // Sort by hasPermission, access count, then sort property
    const sortedResources = [...rootResources].sort((a, b) => {
      // First sort by hasPermission (true comes first)
      if (a.hasPermission !== b.hasPermission) {
        return b.hasPermission ? 1 : -1;
      }

      // Then sort by access count (higher count first)
      const aCount = getAccessCount(String(a.id));
      const bCount = getAccessCount(String(b.id));
      if (aCount !== bCount) {
        return bCount - aCount;
      }

      // Finally sort by sort property
      return (a.sort || 0) - (b.sort || 0);
    });

    // Chia items thành visible và hidden dựa trên responsiveMaxVisibleItems
    const visible = sortedResources.slice(0, responsiveMaxVisibleItems);
    const hidden = [...rootResources];

    setVisibleItems(visible);
    setHiddenItems(hidden);
  }, [responsiveMaxVisibleItems, rootResources]);
  const handleMenuClick = useCallback(
    (item: ResourceShort) => {
      // Kiểm tra quyền trước khi navigate
      if (!item.hasPermission) {
        showNotification('Bạn không có quyền truy cập chức năng này', 'warning');
        return;
      }

      // Increment access count for usage tracking
      incrementAccessCount(String(item.id));

      setRootResource(item);
      setIsModalOpen(false); // Close modal when navigating
      navigate(String(item?.path));
    },
    [navigate, setRootResource]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleModalOpen = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsModalOpen(true);
  }, []);

  // Add keyboard escape handling
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isModalOpen]);

  const dropdownContent = useMemo(() => {
    if (hiddenItems.length === 0) return null;

    return (
      <div className={styles['more-dropdown']}>
        <div className={styles['dropdown-title']}>Tất cả chức năng</div>
        <div className={styles['dropdown-grid']}>
          {hiddenItems.map((item) => (
            <div
              key={item.id}
              className={`${styles['dropdown-item']} ${!item.hasPermission ? styles['no-permission'] : ''}`}
              onClick={() => handleMenuClick(item)}
            >
              <span className={styles['dropdown-icon']}>{renderIcon(item.icon, 24)}</span>
              <span className={styles['dropdown-label']}>{item.name}</span>
              {!item.hasPermission && (
                <span className={styles['lock-icon-dropdown']}>
                  <Lock size={12} color='#64748b' />
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }, [hiddenItems, handleMenuClick]);

  const renderMoreDropdown = useCallback(() => {
    if (hiddenItems.length === 0) return null;

    return (
      <>
        <div
          className={`${styles['menu-item']} ${styles['more-button']}`}
          onClick={handleModalOpen}
          role='button'
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleModalOpen();
            }
          }}
        >
          <MoreOutlined className={styles['more-icon']} />
          <Text className={styles['menu-label']}>Thêm</Text>
          <span className={styles['count-badge']}>{hiddenItems?.length}</span>
        </div>
        <Modal
          open={isModalOpen}
          onCancel={handleModalClose}
          footer={null}
          centered
          width={screens.lg ? Math.min(1100, window.innerWidth * 0.9) : window.innerWidth * 0.95}
          zIndex={9999}
          className='adaptive-horizontal-menu-modal'
        >
          <div>{dropdownContent}</div>
        </Modal>
      </>
    );
  }, [hiddenItems, dropdownContent, handleModalOpen, handleModalClose, isModalOpen, screens.lg]);
  return (
    <div className={`${styles['adaptive-menu']} ${className || ''}`} ref={containerRef}>
      {' '}
      {/* Hiển thị các items visible */}
      {visibleItems.map((item) => (
        <div
          key={item.id}
          className={`${styles['menu-item']} ${!item.hasPermission ? styles['no-permission'] : ''}`}
          onClick={() => handleMenuClick(item)}
        >
          <span className={styles['menu-icon']}>{renderIcon(item.icon)}</span>
          {/* Nếu item có iconPath, sử dụng iconPath thay vì icon */}
          {/* {item.iconPath && <img src={item.iconPath} alt={item.name} className={styles['menu-icon']} />} */}
          <Text className={styles['menu-label']}>{item.name}</Text>
          {!item.hasPermission && (
            <span className={styles['lock-icon-menu']}>
              <Lock size={12} color='#64748b' />
            </span>
          )}
        </div>
      ))}
      {/* Dropdown "More" cho các items hidden */}
      {renderMoreDropdown()}
    </div>
  );
}
